// Creative review, team, lead delivery, data integrations, meetings,
// developer API, Ask Guru history, notifications.
import { DEMO_CLIENT, VERTICALS, addDays, iso, today } from '../client';

const ts = (daysAgo, hh = '15') => `${iso(addDays(today(), -daysAgo))}T${hh}:00:00Z`;

// Placeholder ad artwork drawn as SVG so nothing is fetched.
function adArt(title, sub, c1, c2) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1080" viewBox="0 0 1080 1080">
<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c1}"/><stop offset="1" stop-color="${c2}"/></linearGradient></defs>
<rect width="1080" height="1080" fill="url(#g)"/>
<rect x="80" y="620" width="920" height="320" rx="28" fill="rgba(255,255,255,0.92)"/>
<text x="130" y="740" font-family="Helvetica, Arial, sans-serif" font-size="72" font-weight="700" fill="#1b2a3a">${title}</text>
<text x="130" y="830" font-family="Helvetica, Arial, sans-serif" font-size="44" fill="#41546a">${sub}</text>
<text x="130" y="200" font-family="Helvetica, Arial, sans-serif" font-size="46" font-weight="700" fill="#ffffff">${DEMO_CLIENT.name}</text>
</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function creativeBatches() {
  const asset = (id, status, label, headline, primary, art, comments = []) => ({
    id, asset_status: status, asset_type: 'image', file_url: art, label, headline, primary_text: primary,
    description: 'Free in-home estimate', placement: 'Feed', audience_target: 'advantage_plus', comments,
  });
  return {
    results: [
      {
        id: 4101, status: 'pending_review', vertical_name: 'Bath',
        assets: [
          asset(1, 'pending', 'Bath — Before/After', 'Your New Bath in as Little as 1 Day', 'Tired of that dated tub? See how homeowners in Charlotte are getting a brand-new walk-in shower — installed in as little as one day.', adArt('New Bath in 1 Day', 'Walk-in showers · Tub-to-shower', '#2E6F95', '#7FB8D6')),
          asset(2, 'pending', 'Bath — Financing', '$0 Down, 0% Interest for 18 Months', 'Upgrade your bathroom now and pay over time. Limited appointments available this month.', adArt('$0 Down Financing', 'Limited appointments this month', '#3A7D5C', '#9ED2B4'), [
            { at: ts(1, '14'), role: 'guru', author: 'Lead Gurus Team', text: 'Swapped in the October offer — let us know if 18 months is still right.', pin: { x: 0.42, y: 0.72, t: null } },
          ]),
        ],
      },
      {
        id: 4096, status: 'approved', vertical_name: 'Windows',
        assets: [
          asset(3, 'approved', 'Windows — Energy Savings', 'Cut Drafts Before Winter', 'Replace drafty windows before the cold sets in. Free estimates across the Triangle.', adArt('Cut Drafts Before Winter', 'Free in-home estimate', '#7A4E2D', '#D9A066')),
        ],
      },
    ],
  };
}

function team() {
  return {
    caller_role: 'admin', caller_domain: 'summitridge.example', scope_name: DEMO_CLIENT.name, scope_id: DEMO_CLIENT.id, scope: 'client',
    member_count: 4, member_limit: 8,
    members: [
      { id: 91001, membership_id: 1, first_name: 'Dana', last_name: 'Whitfield', email: DEMO_CLIENT.contact_email, role: 'admin', last_login: ts(0, '13') },
      { id: 91002, membership_id: 2, first_name: 'Marcus', last_name: 'Lee', email: 'marcus@summitridge.example', role: 'editor', last_login: ts(2, '16') },
      { id: 91003, membership_id: 3, first_name: 'Priya', last_name: 'Shah', email: 'priya@summitridge.example', role: 'viewer', last_login: ts(6, '18') },
      { id: 91004, membership_id: 4, first_name: 'Accounts', last_name: 'Payable', email: 'ap@summitridge.example', role: 'viewer', last_login: null },
    ],
  };
}

const pocs = () => [
  { id: 1, role: 'billing', active: true, receives_billing_notifications: true, first_name: 'Accounts', last_name: 'Payable', email: 'ap@summitridge.example' },
  { id: 2, role: 'main', active: true, receives_billing_notifications: true, first_name: 'Dana', last_name: 'Whitfield', email: DEMO_CLIENT.contact_email },
];

const webhookPresets = () => ({
  presets: [{ key: 'generic', name: 'Generic webhook', available: true, default_url: '', default_field_mapping: {}, default_static_values: {}, default_headers: {}, configurable_headers: [], configurable_static_fields: [], email_only: false }],
  lead_fields: [
    { key: 'first_name', label: 'First name', required: true, example: 'Jane' },
    { key: 'last_name', label: 'Last name', required: true, example: 'Smith' },
    { key: 'phone', label: 'Phone', required: true, example: '7045550100' },
    { key: 'email', label: 'Email', required: false, example: 'jane@example.com' },
    { key: 'zip_code', label: 'ZIP', required: true, example: '28277' },
  ],
});

const webhookConfig = () => ({
  results: [{
    id: 501, channel: 'primary', mode: 'live', label: 'CRM — new leads', crm_preset: 'generic', crm_preset_display: 'Generic webhook',
    channel_display: 'Primary', webhook_url: 'https://crm.summitridge.example/api/leads', is_active: true, last_test_success: true,
    field_mapping: { first_name: 'FirstName', last_name: 'LastName', phone: 'Phone', email: 'Email', zip_code: 'Zip' },
    static_values: { LeadSource: 'Lead Gurus' }, comments_key: 'Notes', comments_fields: ['vertical', 'territory'], custom_headers: {},
    send_as_form_encoded: false, auth_type: 'none', success_keyword: 'success', use_raw_json: false, raw_json_template: '',
    routing_rules: [], phone_format: 'PLAIN', datetime_field_settings: {}, datetime_format: '', datetime_timezone: 'America/New_York', vertical_product_codes: {},
  }],
  client_verticals: VERTICALS.map((v) => v.name),
  crm_choice: 'generic',
  email_recipient_count: 2,
});

const emailNotifications = () => ({
  results: [
    { id: 1, name: 'Dana Whitfield', email: DEMO_CLIENT.contact_email, is_active: true, notify_standard_leads: true, notify_self_book_leads: true, conditions: [] },
    { id: 2, name: 'Raleigh office', email: 'raleigh@summitridge.example', is_active: true, notify_standard_leads: true, notify_self_book_leads: true, conditions: [{ field: 'territory', value: 'Raleigh-Durham' }] },
  ],
});

const integrations = () => [{
  id: 301, client_id: DEMO_CLIENT.id, client_name: DEMO_CLIENT.name, integration_type: 'api', enabled: true,
  schedule_type: 'daily', run_time: '06:00:00', last_run_at: ts(0, '10'), last_run_status: 'succeeded', needs_reconnect: false,
}];

const meetingPolicy = () => ({
  allow_self_serve_booking: false, allow_request_form: true, quota_remaining: 2, quota_used: 1, meetings_per_month: 3,
  quota_late_cancellations: 0, quota_resets_at: `${iso(new Date(today().getFullYear(), today().getMonth() + 1, 1))}T04:00:00Z`,
  duration_minutes: 30, max_horizon_days: 21, cancel_restores_quota_within_hours: 24, tier_name: 'Growth', next_tier_name: 'Scale',
  next_tier_threshold: 100000, next_tier_meetings_per_month: 4, basis_spend: 63000, is_access_grant: false, is_welcome_bonus: false,
  backend_data_connected: true,
  office_hours: [
    { weekday_display: 'Tuesday', start_time: '10:00:00', end_time: '16:00:00', timezone: 'America/New_York' },
    { weekday_display: 'Thursday', start_time: '10:00:00', end_time: '16:00:00', timezone: 'America/New_York' },
  ],
});

const meetingHistory = () => ({
  meetings: [{
    id: 1, booking_id: null, started_at: ts(16, '15'), title: 'Monthly performance review', duration_minutes: 30,
    summary: 'Reviewed September results: bath cost per appointment down, windows steady. Agreed to add four ZIPs south of Charlotte and test a financing offer.',
    action_items: ['Add 28025, 28027, 28115, 28117 to Charlotte', 'Launch $0-down financing creative for Bath'], transcript_url: null,
  }],
});

const apiKeys = () => [{
  id: 1, label: 'BI dashboard', scope: 'client', client_name: DEMO_CLIENT.name, parent_company_name: null, is_active: true,
  spend_includes_fees: true, created_at: ts(120), last_used_at: ts(0, '11'),
}];

const chatSessions = () => [
  { id: 'demo-1', title: 'Which market has the best cost per appointment?', updated_at: ts(3), created_by_name: 'Dana Whitfield', client: DEMO_CLIENT.id, client_name: DEMO_CLIENT.name },
];

const chatHistory = () => ({
  turns: [
    { id: 1, role: 'user', content: 'Which market has the best cost per appointment this month?', tool_calls: [], created_at: ts(3, '14'), suggestions: [] },
    { id: 2, role: 'assistant', content: '**Charlotte** is leading this month — bath appointments there are coming in about 12% cheaper than Raleigh-Durham, mostly from a higher set rate on weekday leads. Greensboro is smaller but steady.', tool_calls: [], created_at: ts(3, '14'), suggestions: ['Show me by vertical', 'How is pacing?'] },
  ],
});

const notifications = () => ({
  results: [
    { id: 1, title: 'Creative ready for review', body: '2 new Bath ads are waiting for your approval.', link: '/creative-review', is_read: false, read_at: null, created_at: ts(1), client: { name: DEMO_CLIENT.name } },
    { id: 2, title: 'ZIP changes applied', body: '4 ZIPs added to Charlotte · Bath.', link: '/zip-coverage', is_read: false, read_at: null, created_at: ts(12), client: { name: DEMO_CLIENT.name } },
  ],
  count: 2,
});

export const routes = [
  ['GET', 'app1/creative-review/:slug', () => creativeBatches()],
  ['GET', 'accounts/auth/team', () => team()],
  ['GET', 'dashboard/client-pocs', () => pocs()],
  ['GET', 'app1/webhook-config/presets', () => webhookPresets()],
  ['GET', 'app1/webhook-config/:slug/delivery-log', () => ({ results: [] })],
  ['GET', 'app1/webhook-config/:slug', ({ params }) => (params.get('mode') === 'archived' ? { results: [] } : webhookConfig())],
  ['GET', 'app1/email-notifications/:slug', () => emailNotifications()],
  ['GET', 'dashboard/data-integrations/health', () => integrations()],
  ['GET', 'meetings/clients/:slug/policy', () => meetingPolicy()],
  ['GET', 'meetings/clients/:slug/bookings', () => []],
  ['GET', 'meetings/clients/:slug/meeting-history', () => meetingHistory()],
  ['GET', 'meetings/clients/:slug/availability', () => []],
  ['GET', 'v1/keys', () => apiKeys()],
  ['GET', 'client-inbox/chat-sessions', () => chatSessions()],
  ['GET', 'client-inbox/conversation-turns/history', () => chatHistory()],
  ['GET', 'dashboard/notifications', () => notifications()],
];
